import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { createContext, useContext, useMemo } from 'react';
import { ROUTE_LABS_ORDER_STEP_1 } from '../../../constants/routes';
import DashboardNavigationBar from '../../../components/dashboard/navigation-bar/dashboard-navigation-bar';
import { useFullScreen } from '@/queries/layout.queries';
import {
  useListLabOrdersV1LabsOrdersGet,
  useGetLabOrderV1LabsOrdersOrderIdGet,
} from '@/api/generated-catch-backend/laboratory-orders/laboratory-orders';
import type { LabOrderResponse } from '@/api/generated-catch-backend/model';
import { OrderStatus } from '@/api/generated-catch-backend/model';

// Form schema for all steps
export const labsOrderSchema = z.object({
  // Step 2: User information
  firstName: z
    .string()
    .min(1, 'First name is required')
    .regex(
      /^[a-zA-Z0-9][a-zA-Z0-9\s.\-']*$/,
      'First name must start with a letter or number and can only contain letters, numbers, spaces, hyphens, periods, and apostrophes'
    ),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .regex(
      /^[a-zA-Z0-9][a-zA-Z0-9\s.\-']*$/,
      'Last name must start with a letter or number and can only contain letters, numbers, spaces, hyphens, periods, and apostrophes'
    ),
  email: z.string().email('Please enter a valid email address'),
  phoneNumber: z.string().regex(
    /^\+?1?\d{10,14}$/,
    'Please enter a valid phone number'
  ),
  gender: z.enum(['male', 'female', 'other', 'unknown'], {
    errorMap: () => ({ message: 'Please select a gender' })
  }),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  address: z.object({
    street: z.string().min(1, 'Street address is required'),
    apartmentSuite: z.string().optional(),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(2, 'State is required'),
    zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, 'Please enter a valid ZIP code'),
  }),
});

export type LabsOrderFormData = z.infer<typeof labsOrderSchema>;

// Context to share order data across child routes
interface LabOrderContextValue {
  activeOrder?: LabOrderResponse;
  isLoadingOrder: boolean;
  refetchOrder: () => void;
  refetchOrdersList: () => void;
}

export const LabOrderContext = createContext<LabOrderContextValue | undefined>(undefined);

export function useLabOrder() {
  const context = useContext(LabOrderContext);
  if (!context) {
    throw new Error('useLabOrder must be used within LabOrderContext.Provider');
  }
  return context;
}

export function Page() {
  useFullScreen();
  const location = useLocation();
  const isProduction = import.meta.env.VITE_DEPLOYMENT_TARGET === 'PROD';

  // Fetch list of orders to find if there's an active one
  const { data: ordersData, isLoading: isLoadingOrders, refetch: refetchOrdersList } = useListLabOrdersV1LabsOrdersGet({
    query: {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
    },
  });

  // Find the most recent active order (not cancelled or completed)
  const activeOrderId = useMemo(() => {
    if (!ordersData?.data) return undefined;

    const activeOrder = ordersData.data.find(
      (order) =>
        order.status !== OrderStatus.cancelled &&
        order.status !== OrderStatus.completed &&
        order.status !== OrderStatus.processed
    );

    return activeOrder?.orderId;
  }, [ordersData]);

  const {
    data: activeOrderData,
    isLoading: isLoadingOrderDetails,
    refetch: refetchOrder,
  } = useGetLabOrderV1LabsOrdersOrderIdGet(activeOrderId ?? '', {
    query: {
      enabled: !!activeOrderId,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
    },
  });

  const activeOrder = activeOrderId ? activeOrderData : undefined;

  const isLoadingOrder = isLoadingOrders || isLoadingOrderDetails;

  const methods = useForm<LabsOrderFormData>({
    resolver: zodResolver(labsOrderSchema),
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      gender: undefined as unknown as 'male' | 'female' | 'other' | 'unknown',
      dateOfBirth: '',
      address: {
        street: '',
        apartmentSuite: '',
        city: '',
        state: '',
        zipCode: '',
      },
    },
  });

  // Production check
  if (isProduction) {
    return (
      <>
        <DashboardNavigationBar />
        <div className="w-full">
          <div className="container mx-auto px-4 py-8">
            <div className="mx-auto max-w-4xl">
              <div className="rounded-lg bg-white p-8 text-center shadow-md">
                <div className="mb-6 text-6xl">
                  <span role="img" aria-label="rocket">🚀</span>
                </div>
                <h1 className="mb-4 text-3xl font-bold text-gray-900">
                  This functionality is coming soon!
                </h1>
                <p className="text-gray-600">
                  We're working hard to bring you something amazing.
                  <br />
                  Check back later for updates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Redirect to step 1 if at base /labs/order path
  if (location.pathname === '/labs/order') {
    return <Navigate to={ROUTE_LABS_ORDER_STEP_1} replace />;
  }

  const contextValue: LabOrderContextValue = {
    activeOrder,
    isLoadingOrder,
    refetchOrder,
    refetchOrdersList,
  };

  return (
    <LabOrderContext.Provider value={contextValue}>
      <FormProvider {...methods}>
        <Outlet />
      </FormProvider>
    </LabOrderContext.Provider>
  );
}
